import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import type { ScanConfig, AbortReason, SavedPicture } from './types.js'
import { LiveSession } from './LiveSession.js'
import type { CameraGrant } from './useRequestCameraGrant.js'
import { createShutter, type ShutterPlayFn } from './shutter.js'
import { ProgressBar } from '../components/ProgressBar.js'
import { LiveFindingsChips } from '../components/LiveFindingsChips.js'
import { VerifyingIndicator } from '../components/VerifyingIndicator.js'
import { Button } from '../components/ui/button.js'
import { useDemoStore } from '../state/store.js'

interface Props {
  scanConfig: ScanConfig
  grant: CameraGrant
  onComplete: (findings: unknown) => void
  onAbort: (reason: AbortReason) => void
}

export const LiveCameraScan = ({ scanConfig, grant, onComplete, onAbort }: Props): ReactElement => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const captureCanvasRef = useRef<HTMLCanvasElement>(null)
  const sessionRef = useRef<LiveSession | null>(null)
  const shutterRef = useRef<ShutterPlayFn | null>(null)
  const { dispatch } = useDemoStore()
  const [progressDone, setProgressDone] = useState<string[]>([])
  const [caption, setCaption] = useState<string>('')
  const [flashing, setFlashing] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // Mount the MediaStream onto the <video>. The grant's lifetime is owned
  // by the parent screen via useGrantLifetime — we don't stop the tracks
  // here, only release our local <video> binding.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.srcObject = grant.stream
    // Defer play() until the element actually has metadata. iOS Safari
    // sometimes rejects play() on a MediaStream source before
    // loadedmetadata fires, even with muted+playsInline. Log rather than
    // swallow so a real device failure is visible in the console.
    const start = (): void => {
      video.play().catch(err => { console.warn('[LiveCameraScan] video.play() rejected:', err) })
    }
    if (video.readyState >= 1) start()
    else video.addEventListener('loadedmetadata', start, { once: true })
    return () => {
      // Detach srcObject so the element releases its reference; the grant
      // itself outlives this component (parent owns it).
      video.srcObject = null
    }
  }, [grant])

  // Paint the hidden capture canvas from the <video> each frame so Gemini
  // sees what the user sees. Without this, `LiveSession.sampleFrame` reads
  // an empty canvas and every frame to Gemini is black.
  useEffect(() => {
    let rafId: number | null = null
    let cancelled = false

    const tick = (): void => {
      if (cancelled) return
      const video = videoRef.current
      const canvas = captureCanvasRef.current
      if (video && canvas && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        // Match canvas dims to the native frame so the JPEG sent to Gemini
        // matches what the user sees (and OCR has the most pixels to work with).
        if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth
        if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (ctx) ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)

    return () => {
      cancelled = true
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [grant])

  // Preload the shutter clip once per grant. Decoding happens off the main
  // path; the play function lands in shutterRef when ready and is a no-op
  // until then (early captures just skip the tick, which is fine).
  useEffect(() => {
    let cancelled = false
    void createShutter(grant.audioContext).then(play => {
      if (!cancelled) shutterRef.current = play
    })
    return () => { cancelled = true; shutterRef.current = null }
  }, [grant])

  // Open the LiveSession
  useEffect(() => {
    if (!captureCanvasRef.current) return
    let cancelled = false
    let lastAudioUrl: string | null = null

    const playTurnAudio = (blob: Blob): void => {
      const el = grant.audioElement
      const url = URL.createObjectURL(blob)
      if (lastAudioUrl) URL.revokeObjectURL(lastAudioUrl)
      lastAudioUrl = url
      el.src = url
      el.play().catch(() => { /* iOS gesture restrictions can swallow this; harmless */ })
    }

    const onPictureSaved = (picture: SavedPicture): void => {
      // Always dispatch — the picture is async (downscale finishes after the
      // model may have already called advance_step). The store survives the
      // unmount; gating dispatch on `cancelled` here used to drop the 2nd
      // and 3rd pictures whenever the AI fired multiple save_picture calls
      // followed by advance_step in the same turn.
      dispatch({ type: 'PICTURE_SAVED', picture })
      if (cancelled) return
      shutterRef.current?.()
      setFlashing(true)
      window.setTimeout(() => setFlashing(false), 180)
    }

    LiveSession.open({
      config: scanConfig,
      mediaStream: grant.stream,
      audioStream: new MediaStream(grant.stream.getAudioTracks()),
      hiddenCanvas: captureCanvasRef.current,
      audioContext: grant.audioContext,
      callbacks: {
        onProgress: step => setProgressDone(prev => prev.includes(step) ? prev : [...prev, step]),
        onCaption: text => setCaption(text),
        onFindings: findings => { if (!cancelled) onComplete(findings) },
        onAbort: reason => { if (!cancelled) onAbort(reason) },
        onLivePatch: patch => { if (!cancelled) dispatch({ type: 'LIVE_FINDINGS_PATCH', scan: scanConfig.id, patch }) },
        onPictureSaved,
        onVerifyStart: () => { if (!cancelled) setVerifying(true) },
        onVerifyEnd: () => { if (!cancelled) setVerifying(false) },
        onTurnAudio: blob => { if (!cancelled) playTurnAudio(blob) },
      },
    }).then(s => { sessionRef.current = s }).catch(() => { /* onAbort already fired */ })

    return () => {
      cancelled = true
      sessionRef.current?.close()
      sessionRef.current = null
      if (lastAudioUrl) URL.revokeObjectURL(lastAudioUrl)
    }
  }, [grant, scanConfig, onComplete, onAbort, dispatch])

  const items = scanConfig.progressCheckpoints.map(label => ({
    label,
    state: progressDone.includes(label) ? 'done' as const :
           (progressDone.length === scanConfig.progressCheckpoints.indexOf(label) ? 'active' as const : 'pending' as const),
  }))
  const percent = progressDone.length / Math.max(1, scanConfig.progressCheckpoints.length)

  return (
    <div className="fixed inset-0 bg-foreground/95 flex flex-col">
      <video ref={videoRef} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
      <canvas ref={captureCanvasRef} className="hidden" width={1280} height={720} />

      {/* White flash on save_picture. 180ms total — short enough to read as a shutter. */}
      {flashing && (
        <div className="pointer-events-none absolute inset-0 bg-white opacity-80 transition-opacity duration-150" />
      )}

      {/* Corner reticles — sit between the safe-area edges, sized in vmin so
          they breathe on tall and short viewports alike. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          paddingTop: 'max(env(safe-area-inset-top), 0px)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
        }}
      >
        <div className="absolute top-[18vmin] left-[8vmin] w-10 h-10 border-l-2 border-t-2 border-primary rounded-tl-md" />
        <div className="absolute top-[18vmin] right-[8vmin] w-10 h-10 border-r-2 border-t-2 border-primary rounded-tr-md" />
        <div className="absolute bottom-[36vmin] left-[8vmin] w-10 h-10 border-l-2 border-b-2 border-primary rounded-bl-md" />
        <div className="absolute bottom-[36vmin] right-[8vmin] w-10 h-10 border-r-2 border-b-2 border-primary rounded-br-md" />
      </div>

      {/* Caption + live findings chips + verifying indicator — under the safe-area-top inset so they clear notches. */}
      <div
        className="absolute left-4 right-4 text-center"
        style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        {caption && (
          <span className="inline-block bg-foreground/70 backdrop-blur-md text-background text-sm px-4 py-2 rounded-full max-w-full">
            {caption}
          </span>
        )}
        <LiveFindingsChips scan={scanConfig.id} />
        <VerifyingIndicator visible={verifying} />
      </div>

      {/* Bottom chrome: progress bar + skip pill, both above the home indicator. */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 space-y-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <ProgressBar items={items} percent={percent} />
        <div className="flex justify-center">
          <Button
            variant="ghost"
            onClick={() => sessionRef.current?.cancel()}
            className="bg-foreground/40 backdrop-blur-md text-background hover:bg-foreground/60 hover:text-background min-h-[44px] px-6"
          >
            Skip — I'll enter manually
          </Button>
        </div>
      </div>
    </div>
  )
}
